import React, { useEffect, useState } from "react";
import {
  generatePath,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { observer } from "mobx-react";

import WorkoutDetailsStore from "stores/WorkoutDetailsStore";
import { Page } from "components/Layout";
import Workout from "components/Workout/WorkoutDetails";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import WorkoutNavList from "pages/WorkoutMain/NavList";
import CalendarNavList from "pages/Calendar/NavList";
import ROUTES from "navigation/routes";
import { WEEK } from "constants/common";
import { Background } from "./styles";
import NavList from "pages/Today/NavList/NavList";

type Params = {
  workoutId: string;
  workoutInWorkoutCategoryId?: string;
  categoryId?: string;
  workoutGroup?: string;
  scheduleDate?: string;
};

type Props = SnackbarContextProps & GlobalPopupProps;

const WorkoutDetails: React.FC<Props> = observer(() => {
  const params = useParams<Params>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const categoryId = params.categoryId
    ? Number.parseInt(params.categoryId)
    : undefined;
  const scheduleDate = params.scheduleDate;
  const workoutId = params.workoutId
    ? Number.parseInt(params.workoutId)
    : undefined;
  const workoutGroup = params.workoutGroup;
  const url = location.pathname;
  const workoutInWorkoutCategoryId = params.workoutInWorkoutCategoryId
    ? Number.parseInt(params.workoutInWorkoutCategoryId)
    : undefined;

  useEffect(() => {
    if (workoutId) {
      setIsLoading(true);
      WorkoutDetailsStore.fetch({ workoutId }).finally(() =>
        setIsLoading(false),
      );
    }
  }, [workoutId]);

  const dayOfWeek = React.useMemo(() => {
    const day = Object.keys(WEEK).find(key => WEEK[key] === workoutGroup);
    const parsedDay = day !== undefined ? Number.parseInt(day) : null;
    return typeof parsedDay === "number" ? parsedDay + 1 : null;
  }, [workoutGroup]);

  const isFavorites = url.includes(ROUTES.FAVORITES);
  const isToday = url.includes(ROUTES.TODAY);
  const isWorkout = !isToday && url.includes(ROUTES.WORKOUT);
  const isSearch = url.includes(ROUTES.SEARCH);
  const isCalendar = url.includes(ROUTES.CALENDAR);

  const navList = React.useMemo(() => {
    if (isSearch) {
      return null;
    } else if (isToday) {
      return <NavList />;
    } else if (isWorkout) {
      return <WorkoutNavList />;
    } else if (isCalendar) {
      return <CalendarNavList />;
    }
  }, [isSearch, isToday, isWorkout, isCalendar]);

  const workoutTitle =
    WorkoutDetailsStore.details?.label ||
    WorkoutDetailsStore.details?.title ||
    "";

  const onNavigateToMovement = (movement: any) => {
    const { id, title } = movement;

    if (isWorkout && !isSearch) {
      const path =
        categoryId && workoutGroup
          ? generatePath(
              ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
              {
                categoryId,
                workoutId,
                workoutGroup,
                movementId: id,
              },
            )
          : workoutGroup
          ? generatePath(
              ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS_MOVEMENT_DETAILS,
              {
                workoutId,
                workoutGroup,
                movementId: id,
              },
            )
          : !isFavorites
          ? generatePath(
              ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
              {
                categoryId,
                workoutId,
                movementId: id,
              },
            )
          : generatePath(
              ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS_MOVEMENT_DETAILS,
              {
                workoutId,
                movementId: id,
              },
            );

      navigate(path, { state: { categoryId: categoryId, categoryTitle: location.state?.categoryTitle, id: workoutId, workoutTitle: workoutTitle, movementTitle: title, familyId: location.state?.familyId, familyTitle: location.state?.familyTitle } });
    }

    if (isToday && workoutInWorkoutCategoryId !== undefined) {
      const route = ROUTES.TODAY_WORKOUT_DETAILS_MOVEMENT_DETAILS;

      const defaultParams = { workoutId, movementId: id };
      const params = workoutInWorkoutCategoryId !== undefined
        ? {
            ...defaultParams,
            workoutInWorkoutCategoryId,
          }
        : { ...defaultParams };

      navigate(generatePath(route, params), { state: { categoryId: categoryId, id, title } });
    }

    if (isSearch) {
      navigate(
        generatePath(ROUTES.WORKOUT_SEARCH_WORKOUT_DETAILS_MOVEMENT_DETAILS, {
          workoutId,
          movementId: id,
        }),
        { state: { categoryId: categoryId, id: workoutId, title } },
      );
    }

    if (isCalendar) {
      navigate(
        generatePath(
          ROUTES.CALENDAR_WORKOUT_SCHEDULE_DATE_DETAILS_MOVEMENT_DETAILS,
          {
            workoutId,
            movementId: id,
            scheduleDate,
          },
        ),
        { state: { categoryId: categoryId, id: workoutId, title } },
      );
    }
  };

  const onChangeIsLoading = (value: boolean) => {
    setIsLoading(value);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  return (
    <Page
      isLoading={isLoading}
      title={workoutTitle || "Loading..."}
      topNavigation={navList}
      contentBackground={
        <>
          <Background />
          <Background leftDots />
        </>
      }
    >
      <Workout
        workoutId={workoutId}
        categoryId={categoryId}
        dayOfWeek={dayOfWeek}
        scheduleDate={scheduleDate}
        workoutInWorkoutCategoryId={workoutInWorkoutCategoryId}
        onNavigateToMovement={onNavigateToMovement}
        onChangeIsLoading={onChangeIsLoading}
        onGoBack={onGoBack}
      />
    </Page>
  );
});

export default withGlobalSnackbar(withGlobalPopup(WorkoutDetails));
